import { EditorState } from "draft-js";
import { SuggestionsFilterType } from "draft-js-mention-plugin";
import { EditorPlugin } from "draft-js-plugins-editor";
import { Computed, Observable, ObservableArray } from "knockout";
// eslint-disable-next-line @logicsoftware/ep-custom/no-cross-feature-imports
import type { AppState } from "~/app/reducers";
import { ApprovalMessageStatus, ApproverStatus, MessageTypes } from "~/const/approval";
import { Entities, User } from "~/normalizr/entities";
import { AvailableUsersState } from "~/Redux/AvailableUsers/types";
import { Mention } from "~/sharedComponents/advancedEditor/types/types";
import { IconNames } from "~/sharedComponents/icons";
import { AttachmentSaveMode } from "~/sharedComponents/inputs/filesAttach/types";
import { ResourceGroup } from "~/sharedComponents/select/multiSelect/userSelect/UserGroups";
import { AiCompletionState } from "./ai";
import { MentionEntryProps } from "./components/MentionEntry";
import { FollowersState } from "./followers/types";
import { MBValueState } from "./messageBoardValueReducer";

export type MentionSuggestionItem = Mention & { group?: ResourceGroup };

export type KoUserServerModel = {
    Name: string;
    ObjectId: number;
    Email: string;
    RoleId: number;
    IsDisabledUser: boolean;
};

type UsageSpaceViewModel = {
    isAdmin: Observable<boolean>;
    isSpaceExceeded?: Observable<boolean>;
};

type WrappedMember = {
    member: KoUserServerModel;
    isSelected: (value?: boolean) => boolean;
};

export type KoUserCollectionModel = WrappedMember[];

type UploadFileModelApi = {
    canAddAttachment: Observable<boolean>;
    processExceededFiles: (files: any[]) => any[];
    addFiles: (files: any[], jxhr: any, mode?: any) => void;
    setProgress: (files: any[], progress: number) => void;
    appendFormData: (data: any) => void;
    setAttachmentId: (files: any[], result: any) => void;
    uploadFilesCompleted: (files: any[], usageSpaceViewModel: any) => void;
    uploadFilesFailed: (files: any[]) => void;
    postAfterUploadingCompleted: Observable<boolean>;
    isAllFilesProcessed: Observable<boolean>;
    purePost: F<void>;
    usageSpaceViewModel: Observable<UsageSpaceViewModel>;
    ignoreSpaceExceededError: F<void>;
};

type ApprovalViewModel = {
    approvalEnabled: Observable<boolean>;
    isDataBound: Observable<boolean>;
    isLoading: Observable<boolean>;
    isPopupOpened: Observable<boolean>;
    canCreateUser: Observable<boolean>;
    projectMembers: ObservableArray<WrappedMember>;
    notProjectMembers: ObservableArray<WrappedMember>;
    notSpaceMembers: ObservableArray<WrappedMember>;
    selectedUsers: ObservableArray<WrappedMember>;
    applySelected: F<void>;
    preloadModel: F<void>;
    onUserInvited: F1<User, void>;
};

export type MbOwnerInfo = {
    getInfo: () => ProjectOrTask;
    id: number;
    projectId: number;
    entityType: "Project" | "Task" | "Request" | "Issue";
};

interface Approvers {
    UserId: number;
}

interface Attachments {
    AttachmentId: number;
}

export interface PostMessagePayload extends ProjectOrTask {
    Message: string;
    ParentId?: number;
    MessageType: MessageTypes;
    Approvers?: Approvers[];
    Attachments?: Attachments[];
}

export type PostMessageViewModel = UploadFileModelApi & {
    ownerInfo: MbOwnerInfo;
    editorState: Observable<EditorState>;
    message: Observable<string>;
    files: ObservableArray<UploadFileViewModel>;
    isFocused: Observable<boolean>;
    removeFile: F1<UploadFileViewModel>;
    messageHeight: Observable<number>;
    posting: Observable<boolean>;
    post: F<void>;
    postOrFocus: F<void>;
    approvalViewModel: ApprovalViewModel;
    showApprovalIndicator: Computed<boolean>;
    isArchived?: Observable<boolean>;

    messageBoardViewModel: {
        NotSeenMessagesCount: Observable<number>;
        closeNewMessageIndicator: F<void>;
        scrollToFirstNotSeenMessage: F<void>;
        scrollToActiveApproval: F<void>;
    };
};

export type EditMessageViewModel = {
    files: ObservableArray<FileViewModel>;
    ownerInfo: MbOwnerInfo;
    update: F<void>;
    post: F<void>;
    text: Observable<string>;
    editorState: Observable<EditorState>;
    isFocused: Observable<boolean>;
    cancel: F<void>;
    canUpdate: Computed<boolean>;
};

export type FileViewModel = {
    isFile: boolean;
    isImage: boolean;
    filename: string;
    icon?: IconNames;
    previewSource?: string;
    version: number;
    isShowVersion: Observable<boolean>;
    deleteFromMessage: F;
    isDeleted: Observable<boolean>;
};

export type UploadFileViewModel = {
    isFile: boolean;
    isImage: Blob | boolean;
    name: Observable<string>;
    icon?: IconNames;
    imageSrc?: Observable<string>;
    isProcessed: Observable<boolean>;
    version: Observable<number>;
    isFailed: Observable<boolean>;
    attachmentSaveMode?: AttachmentSaveMode;
    originPath?: Observable<string>;
};

export type AttachedFile = {
    file?: UploadFileViewModel | FileViewModel;
    isImage?: boolean;
    name: string;
    icon?: IconNames;
    path?: string;
    loading?: boolean;
    version: number;
    isVersionVisible: boolean;
    isDeleted?: boolean; // used only for editing message
    isFailed?: boolean; // only for new MB when create message
    attachmentSaveMode?: AttachmentSaveMode;
    originPath?: string;
};

export type MentionPlugin = {
    plugin?: MentionSuggestions;
};

export type MessageOwnerState = {
    // if message for task, then value is projectId for this task,
    // if message for project, then value is projectId for this project,
    projectId: number;
    projectOrTask: ProjectOrTask;
};

export type MentionSuggestions = {
    MentionSuggestions: React.FC<MentionSuggestionsProps>;
} & EditorPlugin;

type MentionSuggestionsProps = {
    onSearchChange: (model: SuggestionsFilterType) => void;
    suggestions: Mention[];
    entryComponent: (props: MentionEntryProps) => JSX.Element;
    onOpen: F;
    onClose: F;
    onAddMention?: F1<Mention, void>;
    unselectable?: any;
};

export type MentionsState = {
    [key: string]: {
        /** ids of available mentions for task/project */
        userIds: number[];
        /**
         * user ids that receive only email notification and can't access task/project.
         * key - userId, value always true
         */
        emailOnlyMentions: Dictionary<boolean>;
    };
};

// list of user ids that should receive email notification about posted messages.
export type MBNotificationRecipients = number[];
export type MBNotificationRecipientsState = MBValueState<MBNotificationRecipients>;

type BaseMessage = {
    Date: Date;
    fullDate?: string;
    formatDate?: string;
    IsNew?: boolean;
    Text?: string;
    UserId: number;
    UserName: string;
};

type WorkflowEventChanges = {
    Field: string;
    Text: string;
};

export type WorkflowEvent = BaseMessage & {
    IsDeletedEvent?: boolean;
    Type: string;
    Changes?: WorkflowEventChanges[];
};

export type MessageFile = {
    date?: Date;
    userName?: string;
    AttachmentID: number;
    CanHaveThumbnail: boolean;
    Name: string;
    DownloadPath: string;
    OpenPath?: string;
    UrlTemplate?: string;
    PreviewPath: string;
    Type: string;
    Version: number;
    icon?: IconNames;
    isShowVersion?: boolean;
    file?: any;
    AttachmentSaveMode: AttachmentSaveMode;
};

export type Approver = {
    UserId: number;
    UserName: string;
    Status: ApproverStatus;
    HasSeen: boolean;
    ActionDate: string;
    statusText?: string;
    IsNew?: boolean;
};

export type ApprovalsModel = {
    Id: number;
    IsActive: boolean;
    approvers: Entities<Approver>;
};

export type Message = BaseMessage & {
    MessageId: number;
    ParentId?: number;
    MessageType?: MessageTypes;
    CanEdit: boolean;
    CanDelete: boolean;
    EditedAt?: Date;
    formatEditedAt?: string;
    EditedByName?: string;
    Files?: number[];
    Approval?: number;
    approvalStatus?: ApprovalMessageStatus;
    isDeleted?: boolean;
    annotation?: MessageAnnotation;
};

export interface ApprovalsServerModel extends Omit<ApprovalsModel, "approvers"> {
    Approvers: Approver[];
}

export interface MessageServerModel extends Omit<Message, "Approval"> {
    Approval?: ApprovalsServerModel;
    Annotation?: MessageAnnotation;
}

export interface MessageAnnotation {
    AnnotationId: number;
    AttachmentId: number;
    Metadata: string;
    ProjectId?: number;
    TaskId?: number;
}

export type MessageEntities = Entities<Message>;
export type FilesEntities = Entities<MessageFile>;

export type MessageOrWorkflowEvent = Message | WorkflowEvent;

export type MessageOption = {
    MessagesVisible: boolean;
    FilesVisible: boolean;
    EventsVisible: boolean;
};

export enum MessagesFilter {
    MessagesVisible = "MessagesVisible",
    FilesVisible = "FilesVisible",
    EventsVisible = "EventsVisible",
}

export type ApprovalsEntities = Entities<ApprovalsModel>;

export type ExpandedAttached = Entities<boolean>;

export type Messages = {
    loaded: boolean;
    projectId: number;
    MessageOptionModel?: MessageOption;
    approvals?: ApprovalsEntities;
    events?: WorkflowEvent[];
    messages: MessageEntities;
    /**
     *  reactions object - key is message id, value (unified) is dictionary of emoji and user ids
     *
     *  example:
     *  reactions: {
     *      1: {
     *          "1f604": [1, 2, 3],
     *      }
     *  }
     */
    reactions: Entities<Entities<number[]>>;
    editMessageId?: number;
    reply?: boolean;
    files?: FilesEntities;
    imagesIds?: number[];
    isApprovalsEnabled?: boolean;
};

export interface ReloadReactionRequest {
    ownerInfo: ProjectOrTask;
    MessageId: number;
}

export interface ReactionRequest extends ReloadReactionRequest {
    Emoji: string;
}

export interface ReactionServerModel {
    MessageId: number;
    Emoji: string;
    UserId: number;
}

export interface Reaction {
    emoji: string;
    counter: number;
    isActive: boolean;
    users: number[];
}

export type MessagesState = Dictionary<Messages>;

export type AttachedVersionDialog = {
    isShowDialog: boolean;
    currentFilename?: string;
};

export type MessageBoardState = {
    messageBoard: {
        mentions: MentionsState;
        notificationRecipients: MBNotificationRecipientsState;
        messages?: MessagesState;
        selfAddedMessageIds?: number[];
        messageInfoToScrollTo?: MessageInfoToScrollTo;
        isLastMessageVisible?: boolean;
        isFirstNewMessageVisible?: boolean;
        attachedVersionDialog?: AttachedVersionDialog;
        expandedAttached?: ExpandedAttached;
        aiCompletion?: AiCompletionState;
    };
};

export type MessageInfoToScrollTo = {
    messageId: number;
    highlight: boolean;
};
export const emptyMessageInfoToScrollTo: MessageInfoToScrollTo = {
    messageId: null,
    highlight: false,
};

export type RootMessageBoardState = AvailableUsersState & MessageBoardState & FollowersState & AppState;

export type ProjectOrTask = {
    taskId?: number;
    projectId?: number;
};

export type ApprovalsRequestPayload = ProjectOrTask & {
    approvalId: number;
};

export type NewMessagesCount = {
    messages: number;
    files: number;
    events: number;
};

export const shownMessagesNumber = 10;

export type ImageDocumentFiles = {
    imagesFiles?: MessageFile[];
    documentFiles?: MessageFile[];
    counterFiles?: number;
};
